import { Box, Link, Skeleton, Stack, Typography, useTheme } from '@chronosphereio/chrono-ui';
import { getDiagnosticsFromPromQLQuery, PromQLEditor } from '@chronosphereio/editors/prometheus';
import { useQuery } from '@tanstack/react-query';
import { CalculationSelection } from './calculation-selection';
import { FilterSelection } from './filter-selection';
import { MetricSelection } from './metric-selection';
import { BuilderProvider, useBuilderContext } from './promql-builder-context';
import { metricTypeUsesRangeFunction } from './promql-builder-model';
import { RangeSelection } from './range-selection';

export type PromQLBuilderProps = {
  value: string;
  onChange: (query: string, options?: { shouldRun?: boolean; shouldTriggerOnChange?: boolean }) => void;
  hideCalculation?: boolean;
  hideRange?: boolean;
  hideQueryHints?: boolean;
};

export function PromQLBuilder({ value, onChange, hideCalculation, hideRange, hideQueryHints }: PromQLBuilderProps) {
  return (
    <BuilderProvider value={value} onChange={onChange} hideRange={hideRange} hideCalculation={hideCalculation}>
      <BuilderBase hideCalculation={hideCalculation} hideRange={hideRange} hideQueryHints={hideQueryHints} />
    </BuilderProvider>
  );
}

function BuilderBase({
  hideCalculation,
  hideRange,
  hideQueryHints,
}: {
  hideCalculation?: boolean;
  hideRange?: boolean;
  hideQueryHints?: boolean;
}) {
  const { isLoading, hasQuery, hasMetric, hasMetricType, hasFilters, hasRange, hasCalculation, metricType } =
    useBuilderContext();

  const showFilters = hasFilters || hasMetric;
  const showCalculation = !hideCalculation && (hasCalculation || hasMetric);
  // Make sure to show the range selection if we don't know the metric type.
  const showRange =
    !hideRange && (hasRange || (hasMetric && (metricTypeUsesRangeFunction(metricType) || !hasMetricType)));

  return (
    <Stack spacing={1}>
      <Stack direction="row" gap={1} alignItems="start" flexWrap="wrap">
        <MetricSelection />
        {!isLoading && showFilters && <FilterSelection />}
      </Stack>
      {!isLoading && (showCalculation || showRange) && (
        <Stack direction="row" gap={1} alignItems="start" flexWrap="wrap">
          {showCalculation && <CalculationSelection />}
          {showRange && <RangeSelection />}
        </Stack>
      )}
      {isLoading && hasQuery ? (
        <Skeleton width="100px" />
      ) : (
        <>
          {!hideQueryHints && <QueryHints />}
          <Preview />
        </>
      )}
    </Stack>
  );
}

function QueryHints() {
  const { value, onChange, promClient } = useBuilderContext();

  const { data: notices } = useQuery(['query-hint', value], () =>
    promClient ? getDiagnosticsFromPromQLQuery(value, promClient.metadataClient) : []
  );

  if (!notices || notices.length === 0) return null;

  return (
    <div>
      {notices.map((notice) => (
        <Stack key={notice.message} direction="row" alignItems="center" spacing={0.5}>
          <Typography variant="body2" color="text.legacySecondary">
            {notice.message}
          </Typography>
          {notice.actions?.map((action) => (
            <Link
              key={action.name}
              type="button"
              component="button"
              onClick={() => {
                const newQuery = notice.fix();
                onChange(newQuery, { shouldTriggerOnChange: true });
              }}
            >
              <Typography variant="body2">Apply hint</Typography>
            </Link>
          ))}
        </Stack>
      ))}
    </div>
  );
}

function Preview() {
  const { value } = useBuilderContext();
  const { theme } = useTheme();

  if (!value) return null;

  return (
    <Box display="flex" alignItems="baseline">
      <Typography variant="body2" color="text.legacySecondary">
        PromQL:
      </Typography>
      <PromQLEditor
        style={{ opacity: 0.8, fontSize: theme.typography.body2.fontSize }}
        readOnly
        disableBorder
        value={value}
        enableLinter={false}
      />
    </Box>
  );
}
